/**
 * Mermaid Diagram Component
 *
 * Renders Mermaid ER diagrams with:
 * - Auto-sizing and zoom controls
 * - Pan and zoom functionality
 * - Export to PNG/SVG
 * - Fullscreen mode
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Download,
  RefreshCw,
  Copy,
  Check,
  ScanSearch,
  Rows3,
  Rows2,
} from 'lucide-react';
import Button from '../ui/Button';

interface MermaidDiagramProps {
  diagram: string;
  className?: string;
  title?: string;
  onError?: (error: string) => void;
}

type Density = 'comfortable' | 'compact';

// Brand-aligned theme tokens matching the Interactive (ReactFlow) view's
// aesthetic: soft entity surfaces with an indigo accent, slate-on-white
// typography, subtle relationship lines.  The previous theme used a dark
// navy primaryColor which made the diagram feel like a debug view.
const THEME_VARIABLES = {
  primaryColor: '#eef2ff',          // entity surface (soft indigo-50)
  primaryTextColor: '#1e293b',      // slate-800
  primaryBorderColor: '#6366f1',    // indigo-500 (matches Interactive edges)
  lineColor: '#94a3b8',             // slate-400
  secondaryColor: '#f8fafc',        // slate-50 (attribute rows)
  tertiaryColor: '#ffffff',
  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  fontSize: '13px',
};

const DENSITY_CONFIG: Record<Density, { entityPadding: number; minEntityWidth: number; minEntityHeight: number }> = {
  comfortable: { entityPadding: 18, minEntityWidth: 120, minEntityHeight: 80 },
  compact:     { entityPadding: 8,  minEntityWidth: 90,  minEntityHeight: 60 },
};

// (Re)initialise Mermaid with the given density.  Called inside renderDiagram
// instead of at module load so the density toggle can take effect.
const configureMermaid = (density: Density) => {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    themeVariables: THEME_VARIABLES,
    er: {
      diagramPadding: 24,
      layoutDirection: 'TB',
      useMaxWidth: true,
      ...DENSITY_CONFIG[density],
    },
    securityLevel: 'loose',
  });
};

// Inject a <style> block into the rendered SVG so we can layer on hover
// highlights, soft shadows, and edge-label polish without forking Mermaid
// itself.  These selectors target the class names Mermaid v10+ emits for
// erDiagram entities, attributes, and relationships.
const enhanceSvg = (rawSvg: string): string => {
  const styleBlock = `<style>
    .er.entityBox {
      fill: #ffffff;
      stroke: #cbd5e1;
      stroke-width: 1.5;
      filter: drop-shadow(0 2px 6px rgba(15, 23, 42, 0.08));
      transition: stroke 180ms ease, filter 180ms ease;
    }
    .er.entityBox:hover {
      stroke: #6366f1;
      stroke-width: 2;
      filter: drop-shadow(0 6px 16px rgba(99, 102, 241, 0.25));
      cursor: pointer;
    }
    .er.entityLabel {
      font-weight: 600;
      fill: #0f172a;
    }
    .er.attributeBoxOdd { fill: #f8fafc; stroke: #e2e8f0; }
    .er.attributeBoxEven { fill: #ffffff; stroke: #e2e8f0; }
    .er.attributeLabel { fill: #475569; }
    .er.relationshipLine {
      stroke: #94a3b8;
      stroke-width: 1.25;
    }
    .er.relationshipLabelBox {
      fill: #ffffff;
      stroke: #e2e8f0;
      rx: 4;
      ry: 4;
    }
    .er.relationshipLabel {
      fill: #475569;
      font-size: 11px;
      font-weight: 500;
    }
  </style>`;
  // Insert immediately after the opening <svg ...> tag so styles scope to
  // this SVG only and don't bleed into other Mermaid diagrams on the page.
  return rawSvg.replace(/<svg([^>]*)>/, `<svg$1>${styleBlock}`);
};

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  diagram,
  className = '',
  title = 'Schema Diagram',
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [density, setDensity] = useState<Density>('comfortable');

  // Render the Mermaid diagram
  const renderDiagram = useCallback(async () => {
    if (!diagram) {
      setError('No diagram content provided');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Density toggle re-applies on every render so the user's choice
      // takes effect immediately when they switch comfortable<->compact.
      configureMermaid(density);

      // Generate unique ID for the diagram
      const id = `mermaid-${Date.now()}`;

      // Render the diagram, then layer on our SVG-level styling for hover
      // highlights, shadows, and edge polish.
      const { svg } = await mermaid.render(id, diagram);
      setSvgContent(enhanceSvg(svg));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to render diagram';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [diagram, density, onError]);

  // Re-render when diagram changes
  useEffect(() => {
    renderDiagram();
  }, [renderDiagram]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.25, 0.25));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
  }, []);

  // Fit-to-screen: reset zoom, then scroll the overflow container so the
  // diagram is centered.  Useful after panning around a large schema.
  const fitToScreen = useCallback(() => {
    setZoom(1);
    // Wait one frame so the zoom-reset has flushed before measuring.
    requestAnimationFrame(() => {
      const scroller = scrollRef.current;
      const svg = containerRef.current?.querySelector('svg') as SVGElement | null;
      if (!scroller || !svg) return;
      const sBox = svg.getBoundingClientRect();
      const cBox = scroller.getBoundingClientRect();
      scroller.scrollLeft = Math.max(0, (sBox.width - cBox.width) / 2);
      scroller.scrollTop = Math.max(0, (sBox.height - cBox.height) / 2);
    });
  }, []);

  // Toggle density (comfortable / compact entity sizing)
  const toggleDensity = useCallback(() => {
    setDensity((prev) => (prev === 'comfortable' ? 'compact' : 'comfortable'));
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // Copy diagram code
  const copyDiagramCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(diagram);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [diagram]);

  // Export as SVG
  const exportSVG = useCallback(() => {
    if (!svgContent) return;

    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/\s+/g, '_')}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [svgContent, title]);

  // Export as PNG
  const exportPNG = useCallback(() => {
    if (!svgContent || !containerRef.current) return;

    const svgElement = containerRef.current.querySelector('svg');
    if (!svgElement) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      canvas.width = img.width * 2; // 2x for better quality
      canvas.height = img.height * 2;
      ctx.scale(2, 2);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = `${title.replace(/\s+/g, '_')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    img.src = url;
  }, [svgContent, title]);

  // Fullscreen container styles
  // `w-full min-w-0` is critical: without it the root div grows to the SVG's
  // natural width (often 1500-2500px for KG schemas), bleeds past the parent
  // card, and visually pushes the chart over neighbouring controls like the
  // Save Draft button.  With it, the SVG is clamped to parent width and the
  // inner `overflow-auto` (below) handles horizontal scroll.
  const containerStyles = isFullscreen
    ? 'fixed inset-0 z-50 bg-white'
    : `relative w-full min-w-0 ${className}`;

  return (
    <div className={containerStyles}>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 border-b border-neutral-200 bg-neutral-50">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm">{title}</h4>
          {!isLoading && !error && (
            <span className="text-xs text-neutral-500">
              Zoom: {Math.round(zoom * 100)}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Zoom controls */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 0.25}
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomReset}
            title="Reset zoom"
          >
            <span className="text-xs font-mono">100%</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 3}
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>

          {/* Fit to screen — resets zoom and centers the SVG inside the
              scroll viewport.  Especially useful after panning a large schema. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={fitToScreen}
            title="Fit to screen"
          >
            <ScanSearch className="w-4 h-4" />
          </Button>

          <div className="w-px h-5 bg-neutral-300 mx-2" />

          {/* Density toggle — comfortable (default) gives entities room to breathe;
              compact packs more on screen for large schemas. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleDensity}
            title={density === 'comfortable' ? 'Switch to compact density' : 'Switch to comfortable density'}
          >
            {density === 'comfortable' ? (
              <Rows3 className="w-4 h-4" />
            ) : (
              <Rows2 className="w-4 h-4" />
            )}
          </Button>

          <div className="w-px h-5 bg-neutral-300 mx-2" />

          {/* Copy code */}
          <Button
            variant="ghost"
            size="sm"
            onClick={copyDiagramCode}
            title="Copy Mermaid code"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </Button>

          {/* Export options */}
          <Button
            variant="ghost"
            size="sm"
            onClick={exportSVG}
            disabled={!svgContent}
            title="Export as SVG"
          >
            <Download className="w-4 h-4" />
          </Button>

          {/* Refresh */}
          <Button
            variant="ghost"
            size="sm"
            onClick={renderDiagram}
            disabled={isLoading}
            title="Refresh diagram"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>

          {/* Fullscreen toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Diagram content */}
      <div
        ref={scrollRef}
        className={`overflow-auto bg-white ${isFullscreen ? 'h-[calc(100%-52px)]' : 'min-h-[400px]'}`}
        style={{ cursor: 'grab' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <div className="flex items-center gap-2 text-neutral-500">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Rendering diagram...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-red-500">
            <p className="font-medium">Failed to render diagram</p>
            <p className="text-sm mt-1 text-neutral-500">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={renderDiagram}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="p-4 flex items-center justify-center"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              minWidth: zoom > 1 ? `${100 / zoom}%` : '100%',
            }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        )}
      </div>

      {/* Fullscreen escape hint */}
      {isFullscreen && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-neutral-800 text-white text-sm px-3 py-1.5 rounded-lg opacity-75">
          Press ESC or click minimize to exit fullscreen
        </div>
      )}
    </div>
  );
};

export default MermaidDiagram;
